import { File, Sponsor } from "@prisma/client";
import Dialog from "@reach/dialog";
import React, {
  cloneElement,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { useFetcher } from "remix";

import { SponsorActionData } from "~/routes/admin/sponsor";
import { Button } from "./Button";
import { FormInput } from "./FormInput";
import { toast } from "./Toast";
import { UploadDialog } from "./UploadDialog";

export type SponsorDialogOwnProps = {
  festivalId: string;
  onFinished?: () => void;
  sponsor?: Sponsor & { image: File };
};

export function SponsorDialog({
  festivalId,
  children,
  onFinished,
  sponsor,
}: PropsWithChildren<SponsorDialogOwnProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const [image, setImage] = useState<File | null>(sponsor?.image ?? null);
  const formRef = useRef<HTMLFormElement>(null);

  const fetcher = useFetcher<SponsorActionData>();
  const toggleOpen = () => {
    setIsOpen((current) => !current);
  };
  const close = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (fetcher.type === "done" && fetcher.data.ok) {
      onFinished?.();
      close();

      toast("Sponsor saved successfully");
    }
  }, [fetcher]);

  return (
    <>
      {React.Children.map(children, (child) => {
        if (!child) {
          return child;
        }
        return cloneElement(child as ReactElement, {
          onClick: toggleOpen,
        });
      })}

      <Dialog
        isOpen={isOpen}
        aria-label="Add or edit Sponsor"
        onDismiss={() => close()}
      >
        <fetcher.Form
          ref={formRef}
          method="post"
          action="/admin/sponsor"
          className="flex flex-col align-top"
        >
          {!!fetcher.data?.formError && (
            <p className="text-red-500">{fetcher.data.formError}</p>
          )}

          {!!sponsor?.id && (
            <input type="hidden" name="id" value={sponsor?.id} />
          )}
          <input type="hidden" name="festivalId" value={festivalId} />

          <FormInput
            label="Name"
            name="name"
            defaultValue={fetcher.data?.fields?.name ?? sponsor?.name}
            fieldError={fetcher.data?.fieldErrors?.name?.[0]}
          />
          <FormInput
            label="URL"
            name="url"
            defaultValue={fetcher.data?.fields?.url ?? sponsor?.url}
            fieldError={fetcher.data?.fieldErrors?.url?.[0]}
          />
          <FormInput
            label="SortOrder"
            name="sortOrder"
            defaultValue={(
              fetcher.data?.fields?.sortOrder ??
              sponsor?.sortOrder ??
              ""
            )?.toString()}
            fieldError={fetcher.data?.fieldErrors?.sortOrder?.[0]}
          />

          <UploadDialog
            label="Image"
            name="imageId"
            value={fetcher.data?.fields?.imageId || image?.id}
            file={image}
            onFileCreated={(file) => setImage(file)}
            fieldError={fetcher.data?.fieldErrors?.imageId?.[0]}
          />

          <input type="hidden" name="_action" value="upsert" />

          <div className="flex flex-row-reverse justify-between">
            <Button type="submit" className="mt-10 ml-auto">
              Speichern
            </Button>
            {!!sponsor && (
              <Button
                variant="destroy"
                className="mt-10"
                name="_action"
                value="destroy"
              >
                Delete
              </Button>
            )}
          </div>
        </fetcher.Form>
      </Dialog>
    </>
  );
}
